// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IButtonStyles } from "@fluentui/react";
import type { ITheme } from "@fluentui/react/lib/Styling";
import { concatStyleSets, mergeStyleSets } from "@fluentui/react/lib/Styling";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import type {
  ActionControlSize,
  StandardControlSemanticColors,
} from "@1js/acui-common";
import { getStandardControlSemanticColorsWithTheme } from "@1js/acui-common";
import type { AppGalleryStyles } from "@1js/acui-gallery";

import type { RibbonTableGalleryButtonStyles } from "../index";

const borderWidth = 4;
const galleryButtonWidth = 74;
export const fullGalleryButtonWidth = galleryButtonWidth + 2 * borderWidth;

/**
 * We use a JS constant inside the CSS rules, therefore we need the below
 * object to be a function. Otherwise, this could have been
 * a constant like the other rules.
 */
const getPreviewGalleryButtonStyles = memoizeFunction(
  (theme: ITheme): IButtonStyles => {
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    return {
      root: {
        boxSizing: "content-box",
        height: 26,
        width: 41,
        padding: 0,
        borderTopWidth: 6,
        borderBottomWidth: 6,
        selectors: {
          ":focus": {
            borderColor: standardControlSemanticColors.galleryHoverFocusColor,
          },
        },
      },
    };
  }
);

const FullGalleryButtonStyles: IButtonStyles = {
  root: {
    boxSizing: "content-box",
    height: 58,
    width: galleryButtonWidth,
    padding: 0,
    borderWidth: 4,
  },
};

const PreviewExpandButtonStyles: IButtonStyles = {
  root: {
    height: 38,
  },
};

const PreviewChangeColorsGalleryButtonStyles: RibbonTableGalleryButtonStyles = {
  tableStyles: {
    fontSize: 5,
    width: 40,
  },
  tableCellStyles: {
    height: 4,
    lineHeight: 4,
    width: 5,
  },
};

const FullChangeColorsGalleryButtonStyles: RibbonTableGalleryButtonStyles = {
  tableStyles: {
    fontSize: 6,
    width: "auto",
    height: "auto",
    borderSpacing: "2px 1px",
  },
  tableCellStyles: {
    height: 14,
    lineHeight: 7,
    width: 22,
  },
};

/**
 * Get the styling function for the table inside of the preview table styles galleryButton.
 * @param { Partial<RibbonTableGalleryStyles> } customStyles - Optional, custom styles to apply to the table and table cells
 */
export const getChangeColorsPreviewGalleryButtonStyles = memoizeFunction(
  (
    customStyles?: Partial<RibbonTableGalleryButtonStyles>
  ): RibbonTableGalleryButtonStyles => {
    return mergeStyleSets(PreviewChangeColorsGalleryButtonStyles, customStyles);
  }
);

/**
 * Get the styling function for the table inside of the full table styles galleryButton.
 * @param { Partial<RibbonTableGalleryStyles> } customStyles - Optional, custom styles to apply to the table and table cells
 */
export const getChangeColorsFullGalleryButtonStyles = memoizeFunction(
  (
    customStyles?: Partial<RibbonTableGalleryButtonStyles>
  ): RibbonTableGalleryButtonStyles => {
    return mergeStyleSets(FullChangeColorsGalleryButtonStyles, customStyles);
  }
);

/**
 * Get the styling for the preview gallery
 * @param { Partial<AppGalleryStyles> } customStyles - Optional, custom styles to apply to gallery
 */
export const getChangeColorsPreviewGalleryStyles = memoizeFunction(
  (
    theme: ITheme,
    customStyles?: Partial<AppGalleryStyles>
  ): Partial<AppGalleryStyles> => {
    return {
      galleryButtonInPreview: concatStyleSets(
        getPreviewGalleryButtonStyles(theme),
        customStyles && customStyles.galleryButtonInPreview
      ),
      previewExpandButton: concatStyleSets(
        PreviewExpandButtonStyles,
        customStyles && customStyles.previewExpandButton
      ),
    };
  }
);

/**
 * Get the styling for the full gallery
 * @param { Partial<AppGalleryStyles> } customStyles - Optional, custom styles to apply to gallery
 */
export const getChangeColorsFullGalleryStyles = memoizeFunction(
  (customStyles?: Partial<AppGalleryStyles>): Partial<AppGalleryStyles> => {
    return {
      galleryButtonInExpanded: concatStyleSets(
        FullGalleryButtonStyles,
        customStyles && customStyles.galleryButtonInExpanded
      ),
    };
  }
);

export const getChangeColorsGallerySizedStyles = memoizeFunction(
  (
    size: ActionControlSize | undefined,
    externalStyles: Partial<AppGalleryStyles> | undefined
  ): Partial<AppGalleryStyles> | {} => {
    switch (size) {
      // Add sized styles as needed
      default:
        return externalStyles || {};
    }
  }
);
